import React, { useEffect, useState } from 'react';
import { translate, getStoredLanguage, hasStoredLanguage, setStoredLanguage } from './i18n';
import './InstallPrompt.css';

// The download buttons on get.wheniseeus.com (landing/) send people here
// with ?instalar=1. Installing a web app only works from the app's own
// origin, so this card is what actually offers "Install" - the browser's
// beforeinstallprompt on Chrome/Edge/Android, or the Share-menu steps on
// iPhone/iPad and Safari on Mac, which have no install API.
const FLAG_KEY = 'wheniseeus-install-requested';

let deferredPrompt = null;
const listeners = new Set();

// Registered at import time, before React mounts: Chrome may fire
// beforeinstallprompt once, early, and it is gone if nobody is listening.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    listeners.forEach((fn) => fn());
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    listeners.forEach((fn) => fn('installed'));
  });
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function platform() {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) {
    return /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua) ? 'ios-other' : 'ios';
  }
  if (/Firefox/.test(ua) && !/Android/.test(ua)) return 'firefox';
  if (/Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR/.test(ua)) return 'mac-safari';
  return 'chromium';
}

// Read once, at import time (before App mounts and reads the language):
// the flag survives the sign-in screen and any redirect that drops the
// query string, and is cleared once handled. The landing's language
// becomes the app's only if the person never picked one.
function readRequest() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('instalar') || params.has('install')) {
      const lang = params.get('lang');
      if ((lang === 'es' || lang === 'en') && !hasStoredLanguage()) setStoredLanguage(lang);
      sessionStorage.setItem(FLAG_KEY, lang || '1');
      params.delete('instalar');
      params.delete('install');
      params.delete('from');
      params.delete('lang');
      const query = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
    }
    return sessionStorage.getItem(FLAG_KEY);
  } catch {
    return null;
  }
}

const initialRequest = typeof window === 'undefined' ? null : readRequest();

function clearRequest() {
  try { sessionStorage.removeItem(FLAG_KEY); } catch {}
}

export default function InstallPrompt() {
  const [request] = useState(initialRequest);
  const [open, setOpen] = useState(() => Boolean(request) && !isStandalone());
  const [canPrompt, setCanPrompt] = useState(() => Boolean(deferredPrompt));
  const [installed, setInstalled] = useState(false);
  const language = request === 'es' || request === 'en' ? request : getStoredLanguage();
  const t = (key) => translate(language, key);
  const kind = platform();

  useEffect(() => {
    const onChange = (what) => {
      setCanPrompt(Boolean(deferredPrompt));
      if (what === 'installed') {
        setInstalled(true);
        clearRequest();
      }
    };
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, []);

  if (!open) return null;

  const close = () => {
    clearRequest();
    setOpen(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    const prompt = deferredPrompt;
    deferredPrompt = null;
    setCanPrompt(false);
    prompt.prompt();
    try {
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
        clearRequest();
      }
    } catch {}
  };

  let body;
  if (installed) {
    body = <p className="install-text">{t('installDone')}</p>;
  } else if (canPrompt) {
    body = (
      <>
        <p className="install-text">{t('installReady')}</p>
        <button type="button" className="install-btn" onClick={install}>{t('installButton')}</button>
      </>
    );
  } else if (kind === 'ios') {
    body = (
      <ol className="install-steps">
        <li>{t('installIosStep1')}</li>
        <li>{t('installIosStep2')}</li>
        <li>{t('installIosStep3')}</li>
      </ol>
    );
  } else if (kind === 'ios-other') {
    body = <p className="install-text">{t('installIosOther')}</p>;
  } else if (kind === 'mac-safari') {
    body = <p className="install-text">{t('installMacSafari')}</p>;
  } else if (kind === 'firefox') {
    body = <p className="install-text">{t('installFirefox')}</p>;
  } else {
    body = <p className="install-text">{t('installMenu')}</p>;
  }

  return (
    <div className="install-card" role="dialog" aria-labelledby="install-title">
      <img src="/logo192.png" alt="" className="install-icon" width="44" height="44" />
      <div className="install-content">
        <p id="install-title" className="install-title">{t('installTitle')}</p>
        {body}
      </div>
      <button type="button" className="install-close" onClick={close} aria-label={t('close')}>×</button>
    </div>
  );
}
